import React, { useState, useEffect, useRef, useCallback, useMemo, forwardRef, useImperativeHandle } from 'react';
import {
  ArrowLeft, Download, Loader2, Sparkles, Send, RefreshCw, Save, Plus, Trash2,
  BarChart3, Table2, FileSpreadsheet, Bold, Italic, AlignLeft, AlignCenter,
  AlignRight, ChevronUp, ChevronDown, ArrowUpDown, Columns, Rows, Type,
  Hash, DollarSign, Percent, Calendar, PanelLeftClose, PanelLeft, MessageSquare, Share2, FileDown, Upload
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { withBackend } from '../utils/api';
import * as XLSX from 'xlsx';
import ReactMarkdown from 'react-markdown';
import { supabaseHelpers } from '../lib/supabase';
import ShareContentModal from './ShareContentModal';
// Sheets deliberately do NOT follow the product-default model (Kimi K3):
// kimi classifies into the 'pro' quota bucket, and free-tier pro sheets
// are 0-blocked BEFORE the credit gate — a kimi default 403'd every free
// sheet out of the box (r87 review). Flash stays the unpicked fallback;
// the flow's own model picker still offers Kimi explicitly.

// Column index to letter (0→A, 1→B, etc.)
const colLetter = (i) => {
  let s = '';
  let n = i;
  while (n >= 0) { s = String.fromCharCode(65 + (n % 26)) + s; n = Math.floor(n / 26) - 1; }
  return s;
};

const TYPE_ICONS = { text: Type, number: Hash, currency: DollarSign, percentage: Percent, date: Calendar };

// Generate a canvas thumbnail of the spreadsheet
function generateSheetThumbnail(sheetData, headerColor, title) {
  const W = 480, H = 300;
  const canvas = document.createElement('canvas');
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext('2d');

  // Background
  ctx.fillStyle = '#f8fafc';
  ctx.fillRect(0, 0, W, H);

  // Title bar
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, W, 36);
  ctx.strokeStyle = '#e2e8f0';
  ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(0, 36); ctx.lineTo(W, 36); ctx.stroke();

  ctx.fillStyle = '#1e293b';
  ctx.font = 'bold 13px Inter, Arial, sans-serif';
  ctx.textBaseline = 'middle';
  ctx.fillText(title || 'Spreadsheet', 12, 18);

  // Dims label
  const dims = `${sheetData.rows?.length || 0} rows × ${sheetData.columns?.length || 0} cols`;
  ctx.fillStyle = '#94a3b8';
  ctx.font = '10px Inter, Arial, sans-serif';
  ctx.fillText(dims, W - ctx.measureText(dims).width - 12, 18);

  // Spreadsheet area
  const tableTop = 36;
  const rowH = 24;
  const cols = sheetData.columns || [];
  const rows = sheetData.rows || [];
  const maxCols = Math.min(cols.length, 6);
  const maxRows = Math.min(rows.length, 9);
  const rowNumW = 32;
  const colW = (W - rowNumW) / maxCols;

  // Row number column bg
  ctx.fillStyle = '#f1f5f9';
  ctx.fillRect(0, tableTop, rowNumW, H - tableTop);

  // Header row (colored)
  ctx.fillStyle = headerColor.bg;
  ctx.fillRect(rowNumW, tableTop, W - rowNumW, rowH);

  // Header text
  ctx.fillStyle = headerColor.text;
  ctx.font = 'bold 10px Inter, Arial, sans-serif';
  ctx.textBaseline = 'middle';
  for (let c = 0; c < maxCols; c++) {
    const text = cols[c] || '';
    const x = rowNumW + c * colW + 6;
    const maxTextW = colW - 12;
    ctx.save();
    ctx.beginPath(); ctx.rect(x, tableTop, maxTextW, rowH); ctx.clip();
    ctx.fillText(text, x, tableTop + rowH / 2);
    ctx.restore();
  }

  // Header row number
  ctx.fillStyle = headerColor.bg + '40';
  ctx.fillRect(0, tableTop, rowNumW, rowH);

  // Grid lines and data rows
  for (let r = 0; r < maxRows; r++) {
    const y = tableTop + (r + 1) * rowH;
    if (y + rowH > H) break;

    // Alternating row bg
    ctx.fillStyle = r % 2 === 0 ? '#ffffff' : '#f8fafc';
    ctx.fillRect(rowNumW, y, W - rowNumW, rowH);

    // Row number
    ctx.fillStyle = '#94a3b8';
    ctx.font = '9px Inter, Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(String(r + 1), rowNumW / 2, y + rowH / 2);
    ctx.textAlign = 'left';

    // Cell values
    ctx.fillStyle = '#334155';
    ctx.font = '10px Inter, Arial, sans-serif';
    const row = rows[r] || [];
    for (let c = 0; c < maxCols; c++) {
      const val = row[c];
      const text = val === null || val === undefined ? '' : String(val);
      const x = rowNumW + c * colW + 6;
      const maxTextW = colW - 12;
      ctx.save();
      ctx.beginPath(); ctx.rect(x, y, maxTextW, rowH); ctx.clip();
      ctx.fillText(text, x, y + rowH / 2);
      ctx.restore();
    }

    // Horizontal line
    ctx.strokeStyle = '#e2e8f0';
    ctx.lineWidth = 0.5;
    ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke();
  }

  // Vertical grid lines
  ctx.strokeStyle = '#e2e8f0';
  ctx.lineWidth = 0.5;
  ctx.beginPath(); ctx.moveTo(rowNumW, tableTop); ctx.lineTo(rowNumW, H); ctx.stroke();
  for (let c = 1; c < maxCols; c++) {
    const x = rowNumW + c * colW;
    ctx.beginPath(); ctx.moveTo(x, tableTop); ctx.lineTo(x, H); ctx.stroke();
  }

  // Border
  ctx.strokeStyle = '#cbd5e1';
  ctx.lineWidth = 1;
  ctx.strokeRect(0, 0, W, H);

  // "Sheets" badge
  const badgeW = 50, badgeH = 18;
  ctx.fillStyle = headerColor.bg;
  const bx = W - badgeW - 8, by = H - badgeH - 8;
  ctx.beginPath();
  ctx.roundRect(bx, by, badgeW, badgeH, 4);
  ctx.fill();
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 9px Inter, Arial, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('Sheets', bx + badgeW / 2, by + badgeH / 2);
  ctx.textAlign = 'left';

  return canvas.toDataURL('image/jpeg', 0.85);
}

// Color palette for styled headers
const HEADER_COLORS = [
  { bg: '#059669', text: '#ffffff' },  // emerald
  { bg: '#0891b2', text: '#ffffff' },  // cyan
  { bg: '#7c3aed', text: '#ffffff' },  // violet
  { bg: '#db2777', text: '#ffffff' },  // pink
  { bg: '#d97706', text: '#ffffff' },  // amber
  { bg: '#2563eb', text: '#ffffff' },  // blue
  { bg: '#dc2626', text: '#ffffff' },  // red
  { bg: '#16a34a', text: '#ffffff' },  // green
];

const SheetsWorkspace = forwardRef(function SheetsWorkspace({ preloadedData, onBack, onFullscreen, userId, onComplete, onUpdate, existingProjectId, externalChatMode, onChatMessage, chatMessages, onUsageRefresh }, ref) {
  const [phase, setPhase] = useState(preloadedData?.savedSheetData ? 'ready' : 'generating');
  const [sheetData, setSheetData] = useState(preloadedData?.savedSheetData || null);
  const [title, setTitle] = useState(preloadedData?.savedTitle || 'Spreadsheet');
  const [activeTab, setActiveTab] = useState('spreadsheet');
  const [editPrompt, setEditPrompt] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [projectId, setProjectId] = useState(existingProjectId || null);
  const [chatHistory, setChatHistory] = useState([]);
  const [editingCell, setEditingCell] = useState(null);
  const [selectedCell, setSelectedCell] = useState(null);
  const [generationStatus, setGenerationStatus] = useState('');
  const [aiPanelOpen, setAiPanelOpen] = useState(true);
  const [cellFormats, setCellFormats] = useState({});  // { "r,c": { bold, italic, align, color } }
  const [contextMenu, setContextMenu] = useState(null); // { x, y, row, col }

  const chatEndRef = useRef(null);
  const editTextareaRef = useRef(null);
  const hasAutoSavedRef = useRef(!!existingProjectId);
  const canvasRefs = useRef({});
  const chartInstancesRef = useRef({});
  const fileInputRef = useRef(null);

  const questionnaireData = preloadedData?.questionnaireData;
  const flowConfig = preloadedData?.flowConfig;

  // Auto-generate on mount
  useEffect(() => {
    if (preloadedData?.savedSheetData) return;
    generateSheetData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    onFullscreen?.(true);
    return () => onFullscreen?.(false);
  }, [onFullscreen]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatHistory]);

  const generateSheetData = async () => {
    setPhase('generating');
    setGenerationStatus('Generating spreadsheet data...');
    try {
      const response = await fetch(withBackend('/api/generate-sheet-data'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: flowConfig?.prompt || 'Create a sample spreadsheet',
          html_model: flowConfig?.htmlModel || 'flash',
          language: flowConfig?.language || 'en',
          excel_data: flowConfig?.excelData || null,
          special_instructions: questionnaireData?.specialInstructions || '',
          generate_sample_data: flowConfig?.generateSampleData || 'yes',
          brand_identity_id: flowConfig?.brandIdentityId || '',
          user_id: userId || '',
        }),
      });
      if (response.status === 403) {
        const err = await response.json().catch(() => ({}));
        if (err.error === 'usage_limit_exceeded') {
          toast.error(err.details?.reason === 'trial_expired'
            ? 'Your 7-day trial has ended. Upgrade to keep creating.'
            : 'You\'ve reached your monthly limit for this generation type. Upgrade for more.');
          setPhase('error');
          return;
        }
      }
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to generate data');
      }
      const data = await response.json();
      if (data.columns && data.rows) {
        setSheetData(data);
        setTitle(data.title || 'Spreadsheet');
        setPhase('ready');
        onUsageRefresh?.();
        onChatMessage?.('Spreadsheet generated! You can now edit it or ask questions about it.');
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Sheet generation error:', error);
      toast.error(error.message || 'Failed to generate spreadsheet');
      setPhase('error');
    }
  };

  // ─── Cell operations ───
  const handleCellChange = useCallback((rowIndex, colIndex, value) => {
    setSheetData(prev => {
      if (!prev) return prev;
      const newRows = prev.rows.map((row, ri) =>
        ri === rowIndex ? row.map((cell, ci) => ci === colIndex ? value : cell) : [...row]
      );
      return { ...prev, rows: newRows };
    });
  }, []);

  const handleHeaderChange = useCallback((colIndex, value) => {
    setSheetData(prev => {
      if (!prev) return prev;
      const newColumns = [...prev.columns];
      newColumns[colIndex] = value;
      return { ...prev, columns: newColumns };
    });
  }, []);

  const addRow = useCallback(() => {
    setSheetData(prev => {
      if (!prev) return prev;
      const newRow = prev.columns.map((_, i) => {
        const type = prev.column_types?.[i] || 'text';
        return type === 'number' || type === 'currency' || type === 'percentage' ? 0 : '';
      });
      return { ...prev, rows: [...prev.rows, newRow] };
    });
  }, []);

  const deleteRow = useCallback((rowIndex) => {
    setSheetData(prev => {
      if (!prev) return prev;
      return { ...prev, rows: prev.rows.filter((_, i) => i !== rowIndex) };
    });
  }, []);

  const addColumn = useCallback(() => {
    setSheetData(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        columns: [...prev.columns, `Column ${prev.columns.length + 1}`],
        column_types: [...(prev.column_types || []), 'text'],
        rows: prev.rows.map(row => [...row, '']),
      };
    });
  }, []);

  const deleteColumn = useCallback((colIndex) => {
    setSheetData(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        columns: prev.columns.filter((_, i) => i !== colIndex),
        column_types: (prev.column_types || []).filter((_, i) => i !== colIndex),
        rows: prev.rows.map(row => row.filter((_, i) => i !== colIndex)),
        charts: (prev.charts || []).filter(c => c.xColumn !== colIndex && !c.yColumns?.includes(colIndex)),
      };
    });
  }, []);

  const insertColumnAt = useCallback((colIndex) => {
    setSheetData(prev => {
      if (!prev) return prev;
      const newCols = [...prev.columns];
      newCols.splice(colIndex, 0, `Column ${prev.columns.length + 1}`);
      const newTypes = [...(prev.column_types || prev.columns.map(() => 'text'))];
      newTypes.splice(colIndex, 0, 'text');
      const newRows = prev.rows.map(row => { const r = [...row]; r.splice(colIndex, 0, ''); return r; });
      return { ...prev, columns: newCols, column_types: newTypes, rows: newRows };
    });
  }, []);

  const insertRowAt = useCallback((rowIndex) => {
    setSheetData(prev => {
      if (!prev) return prev;
      const newRow = prev.columns.map((_, i) => {
        const type = prev.column_types?.[i] || 'text';
        return type === 'number' || type === 'currency' || type === 'percentage' ? 0 : '';
      });
      const newRows = [...prev.rows];
      newRows.splice(rowIndex, 0, newRow);
      return { ...prev, rows: newRows };
    });
  }, []);

  const sortColumn = useCallback((colIndex, direction) => {
    setSheetData(prev => {
      if (!prev) return prev;
      const type = prev.column_types?.[colIndex] || 'text';
      const sorted = [...prev.rows].sort((a, b) => {
        let va = a[colIndex], vb = b[colIndex];
        if (type === 'number' || type === 'currency' || type === 'percentage') {
          va = parseFloat(va) || 0; vb = parseFloat(vb) || 0;
        } else { va = String(va || ''); vb = String(vb || ''); }
        if (va < vb) return direction === 'asc' ? -1 : 1;
        if (va > vb) return direction === 'asc' ? 1 : -1;
        return 0;
      });
      return { ...prev, rows: sorted };
    });
  }, []);

  const changeColumnType = useCallback((colIndex, newType) => {
    setSheetData(prev => {
      if (!prev) return prev;
      const newTypes = [...(prev.column_types || prev.columns.map(() => 'text'))];
      newTypes[colIndex] = newType;
      return { ...prev, column_types: newTypes };
    });
  }, []);

  // ─── File upload (CSV/XLSX) ───
  const handleFileUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    const ext = file.name.split('.').pop().toLowerCase();
    if (!['csv', 'xlsx', 'xls'].includes(ext)) {
      toast.error('Please upload a CSV or Excel file');
      return;
    }

    toast.loading('Importing spreadsheet...', { id: 'file-upload' });

    try {
      if (ext === 'csv') {
        // Parse CSV client-side
        const text = await file.text();
        const lines = text.split(/\r?\n/).filter(l => l.trim());
        if (lines.length === 0) throw new Error('Empty file');

        // Simple CSV parser respecting quoted fields
        const parseLine = (line) => {
          const result = [];
          let current = '';
          let inQuotes = false;
          for (let i = 0; i < line.length; i++) {
            const ch = line[i];
            if (ch === '"') { inQuotes = !inQuotes; }
            else if (ch === ',' && !inQuotes) { result.push(current.trim()); current = ''; }
            else { current += ch; }
          }
          result.push(current.trim());
          return result;
        };

        const headers = parseLine(lines[0]);
        const rows = lines.slice(1, 1001).map(l => {
          const cells = parseLine(l);
          return headers.map((_, i) => {
            const val = cells[i] || '';
            const num = Number(val);
            return val !== '' && !isNaN(num) && isFinite(num) ? num : val;
          });
        });

        // Auto-detect column types
        const colTypes = headers.map((_, ci) => {
          const sample = rows.slice(0, 20).map(r => r[ci]).filter(v => v !== '');
          if (sample.every(v => typeof v === 'number')) return 'number';
          return 'text';
        });

        setSheetData({ columns: headers, column_types: colTypes, rows, charts: [] });
        setTitle(file.name.replace(/\.csv$/i, ''));
        setPhase('ready');
        toast.success(`Imported ${rows.length} rows from CSV`, { id: 'file-upload' });
      } else {
        // Send XLSX to backend for parsing
        const formData = new FormData();
        formData.append('file', file);
        formData.append('extract_type', 'excel');

        const response = await fetch(withBackend('/api/content/extract'), {
          method: 'POST',
          body: formData,
        });
        if (!response.ok) throw new Error('Failed to parse Excel file');
        const result = await response.json();

        if (result.excel_data && result.excel_data.length > 0) {
          const sheet = result.excel_data[0]; // Use first sheet
          const rows = sheet.rows.map(row =>
            row.map(val => {
              const num = Number(val);
              return val !== '' && !isNaN(num) && isFinite(num) ? num : val;
            })
          );

          const colTypes = (sheet.headers || []).map((_, ci) => {
            const sample = rows.slice(0, 20).map(r => r[ci]).filter(v => v !== '');
            if (sample.every(v => typeof v === 'number')) return 'number';
            return 'text';
          });

          setSheetData({
            columns: sheet.headers || [],
            column_types: colTypes,
            rows,
            charts: [],
          });
          setTitle(file.name.replace(/\.(xlsx|xls)$/i, ''));
          setPhase('ready');
          toast.success(`Imported ${rows.length} rows from ${result.excel_data.length > 1 ? sheet.sheet_name : 'Excel'}`, { id: 'file-upload' });
        } else {
          throw new Error('No data found in file');
        }
      }
    } catch (err) {
      console.error('File upload error:', err);
      toast.error(err.message || 'Failed to import file', { id: 'file-upload' });
    }
  }, []);

  // ─── Cell formatting ───
  const toggleFormat = useCallback((prop) => {
    if (!selectedCell) return;
    const key = `${selectedCell.row},${selectedCell.col}`;
    setCellFormats(prev => ({
      ...prev,
      [key]: { ...prev[key], [prop]: !(prev[key]?.[prop]) }
    }));
  }, [selectedCell]);

  const setAlignment = useCallback((align) => {
    if (!selectedCell) return;
    const key = `${selectedCell.row},${selectedCell.col}`;
    setCellFormats(prev => ({
      ...prev,
      [key]: { ...prev[key], align }
    }));
  }, [selectedCell]);

  const setTextColor = useCallback((color) => {
    if (!selectedCell) return;
    const key = `${selectedCell.row},${selectedCell.col}`;
    setCellFormats(prev => ({
      ...prev,
      [key]: { ...prev[key], color }
    }));
  }, [selectedCell]);

  const getCellFormat = useCallback((row, col) => {
    return cellFormats[`${row},${col}`] || {};
  }, [cellFormats]);

  // ─── Format cell value for display ───
  const formatCellValue = useCallback((value, colIndex) => {
    if (value === null || value === undefined) return '';
    const type = sheetData?.column_types?.[colIndex] || 'text';
    if (type === 'currency') {
      const num = typeof value === 'number' ? value : parseFloat(value);
      return isNaN(num) ? value : `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    if (type === 'percentage') {
      const num = typeof value === 'number' ? value : parseFloat(value);
      return isNaN(num) ? value : `${(num * 100).toFixed(1)}%`;
    }
    if (type === 'number') {
      const num = typeof value === 'number' ? value : parseFloat(value);
      return isNaN(num) ? value : num.toLocaleString();
    }
    return String(value);
  }, [sheetData?.column_types]);

  // Pick a header color scheme based on title hash
  const headerColor = useMemo(() => {
    if (!title) return HEADER_COLORS[0];
    let hash = 0;
    for (let i = 0; i < title.length; i++) hash = ((hash << 5) - hash) + title.charCodeAt(i);
    return HEADER_COLORS[Math.abs(hash) % HEADER_COLORS.length];
  }, [title]);

  // Expose methods for external chat mode and UnifiedWorkspace toolbar
  useImperativeHandle(ref, () => ({
    // options accepted for signature parity with other workspaces; reference
    // images don't apply to spreadsheet data edits.
    sendEdit: (message, _options) => handleEditRequest(message),
    getChatHistory: () => chatHistory,
    getData: () => sheetData,
    save: () => { setIsSaving(true); handleSaveProject().finally(() => setIsSaving(false)); },
    share: () => setShowShareModal(true),
    present: () => {
      // Sheets don't have a "present" mode — just toast a hint
      toast.info('Use the Visualize tab to view charts fullscreen');
    },
    exportCsv: () => handleExportXLSX(),
    exportXlsx: () => handleExportXLSX(),
    exportPdf: () => handleExportPDF(),
    exportPng: async () => {
      // Generate a screenshot of the spreadsheet as PNG using the thumbnail generator
      if (!sheetData) return;
      const dataUrl = generateSheetThumbnail(sheetData, headerColor, title);
      const link = document.createElement('a');
      link.download = `${title || 'spreadsheet'}.png`;
      link.href = dataUrl;
      link.click();
      toast.success('PNG downloaded!');
    },
  }));

  // ─── AI Edit ───
  const handleEditRequest = async (messageOverride) => {
    const request = (messageOverride || editPrompt).trim();
    if (!request) return;
    setEditPrompt('');
    setIsEditing(true);
    setChatHistory(prev => [...prev, { role: 'user', content: request, timestamp: Date.now() }]);
    try {
      const response = await fetch(withBackend('/api/edit-sheet-data'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ edit_prompt: request, current_data: sheetData, html_model: flowConfig?.htmlModel || 'flash' }),
      });
      if (!response.ok) throw new Error('Failed to update spreadsheet');
      const data = await response.json();
      if (data.columns && data.rows) {
        setSheetData(data);
        if (data.title) setTitle(data.title);
        setChatHistory(prev => [...prev, { role: 'bot', content: 'Spreadsheet updated.', timestamp: Date.now() }]);
        onChatMessage?.('Spreadsheet updated.');
        toast.success('Spreadsheet updated!');
      } else { throw new Error('Invalid response'); }
    } catch {
      toast.error('Failed to update spreadsheet');
      setChatHistory(prev => [...prev, { role: 'bot', content: 'Failed to update. Please try again.', timestamp: Date.now(), error: true }]);
      onChatMessage?.('Failed to update. Please try again.');
    } finally { setIsEditing(false); }
  };

  // ─── Save (explicit only — no auto-save on every cell edit) ───
  const handleSaveProject = useCallback(async () => {
    if (!userId || !sheetData) return;
    try {
      // Generate thumbnail
      const thumbnail = generateSheetThumbnail(sheetData, headerColor, title);

      const projectData = {
        type: 'sheets', sheet_data: sheetData, title,
        row_count: sheetData.rows?.length || 0, column_count: sheetData.columns?.length || 0,
        metadata: { generationMode: flowConfig?.generationMode, questionnaireData, updated_at: new Date().toISOString(), chatHistory: chatMessages || [] }
      };
      if (projectId) {
        const { error } = await supabaseHelpers.updateProject(projectId, { name: title, analysis_data: projectData, status: 'completed', thumbnail_url: thumbnail });
        if (error) throw error;
        if (onUpdate) onUpdate(projectId, projectData);
        toast.success('Saved!');
      } else {
        const { data, error } = await supabaseHelpers.createProject({ user_id: userId, name: title, client_name: 'Sheets', type: 'sheets', status: 'completed', slide_count: 1, analysis_data: projectData, thumbnail_url: thumbnail });
        if (error) throw error;
        if (data) {
          setProjectId(data.id); hasAutoSavedRef.current = true;
          if (onComplete) onComplete({ projectId: data.id, sheetsData: projectData });
          toast.success('Saved!');
        }
      }
    } catch (error) { console.error('Save error:', error); toast.error(`Failed to save: ${error.message || 'Unknown error'}`); }
  }, [userId, sheetData, title, projectId, flowConfig, questionnaireData, onUpdate, onComplete, headerColor, chatMessages]);

  // ─── Export ───
  const handleExportXLSX = useCallback(() => {
    if (!sheetData) return;
    const wb = XLSX.utils.book_new();
    const wsData = [sheetData.columns, ...sheetData.rows];
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Style header row
    const headerStyle = { font: { bold: true, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: headerColor.bg.replace('#', '') } }, alignment: { horizontal: 'center' }, border: { bottom: { style: 'thin', color: { rgb: '334155' } } } };
    const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
    for (let c = range.s.c; c <= range.e.c; c++) {
      const addr = XLSX.utils.encode_cell({ r: 0, c });
      if (ws[addr]) ws[addr].s = headerStyle;
    }

    // Auto-width columns
    const colWidths = sheetData.columns.map((col, i) => {
      let max = col.length;
      sheetData.rows.forEach(row => { const len = String(row[i] ?? '').length; if (len > max) max = len; });
      return { wch: Math.min(max + 4, 40) };
    });
    ws['!cols'] = colWidths;

    // Apply cell formats (bold, italic, alignment)
    sheetData.rows.forEach((row, ri) => {
      row.forEach((_, ci) => {
        const fmt = cellFormats[`${ri},${ci}`];
        if (fmt) {
          const addr = XLSX.utils.encode_cell({ r: ri + 1, c: ci });
          if (!ws[addr]) return;
          ws[addr].s = {
            ...(ws[addr].s || {}),
            font: { bold: !!fmt.bold, italic: !!fmt.italic, ...(fmt.color ? { color: { rgb: fmt.color.replace('#', '') } } : {}) },
            alignment: fmt.align ? { horizontal: fmt.align } : undefined,
          };
        }
      });
    });

    XLSX.utils.book_append_sheet(wb, ws, title || 'Sheet1');
    XLSX.writeFile(wb, `${title || 'spreadsheet'}.xlsx`);
    toast.success('Excel file downloaded!');
  }, [sheetData, title, headerColor, cellFormats]);

  // Export as PDF — renders sheet data as a styled HTML table, sends to Playwright
  const handleExportPDF = useCallback(async () => {
    if (!sheetData) return;
    toast.loading('Generating PDF...', { id: 'pdf-export' });
    try {
      const escHtml = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      const headerRow = sheetData.columns.map(c => `<th style="padding:8px 12px;background:#1e293b;color:#f8fafc;border:1px solid #334155;font-size:13px;white-space:nowrap">${escHtml(c)}</th>`).join('');
      const bodyRows = sheetData.rows.map((row, ri) =>
        `<tr>${row.map(cell => `<td style="padding:6px 12px;border:1px solid #e2e8f0;font-size:12px;background:${ri % 2 === 0 ? '#ffffff' : '#f8fafc'}">${escHtml(cell)}</td>`).join('')}</tr>`
      ).join('');
      const tableHtml = `<!DOCTYPE html><html><head><style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:system-ui,-apple-system,sans-serif;padding:32px;background:#fff}</style></head><body>
        <h2 style="margin-bottom:16px;font-size:18px;color:#0f172a">${escHtml(title || 'Spreadsheet')}</h2>
        <table style="border-collapse:collapse;width:100%"><thead><tr>${headerRow}</tr></thead><tbody>${bodyRows}</tbody></table>
      </body></html>`;

      const response = await fetch(withBackend('/export-slides-as-pdf'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slides: [tableHtml], presentation_title: title || 'spreadsheet', width: 1200, height: 800, orientation: 'landscape' }),
      });
      if (!response.ok) throw new Error('PDF generation failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title || 'spreadsheet'}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('PDF downloaded!', { id: 'pdf-export' });
    } catch (err) {
      console.error('PDF export error:', err);
      toast.error('PDF export failed', { id: 'pdf-export' });
    }
  }, [sheetData, title]);

  // ─── Chart configs (memoized — only updates when data changes) ───
  const chartConfigs = useMemo(() => {
    if (!sheetData?.charts || !sheetData.rows?.length) return [];
    return sheetData.charts.map((chart, i) => {
      const xCol = chart.xColumn ?? 0;
      const yColumns = chart.yColumns || [1];
      const labels = sheetData.rows.map(row => String(row[xCol] ?? ''));
      const defaultColors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];
      const colors = chart.colors || defaultColors;
      const datasets = yColumns.map((yCol, di) => ({
        label: sheetData.columns[yCol] || `Series ${di + 1}`,
        data: sheetData.rows.map(row => { const val = row[yCol]; return typeof val === 'number' ? val : parseFloat(val) || 0; }),
        backgroundColor: chart.type === 'pie' || chart.type === 'doughnut'
          ? colors.slice(0, sheetData.rows.length) : colors[di % colors.length] + '99',
        borderColor: colors[di % colors.length],
        borderWidth: chart.type === 'line' || chart.type === 'area' ? 2 : 1,
        fill: chart.type === 'area', tension: 0.3,
      }));
      return { id: `chart-${i}`, title: chart.title || `Chart ${i + 1}`, type: chart.type === 'area' ? 'line' : chart.type || 'bar', data: { labels, datasets } };
    });
  }, [sheetData]);

  // Render/re-render charts when switching to visualize tab or when data changes
  const chartsRenderedRef = useRef(null);
  useEffect(() => {
    if (activeTab !== 'visualize' || !chartConfigs.length) return;

    // Small delay to ensure canvas elements are visible and measurable
    const timer = setTimeout(() => {
      const loadChartJS = () => new Promise((resolve) => {
        if (window.Chart) { resolve(window.Chart); return; }
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/chart.js@4.4.7/dist/chart.umd.min.js';
        script.onload = () => resolve(window.Chart);
        document.head.appendChild(script);
      });

      loadChartJS().then(Chart => {
        // Destroy all existing chart instances first
        Object.values(chartInstancesRef.current).forEach(inst => { try { inst.destroy(); } catch {} });
        chartInstancesRef.current = {};

        chartConfigs.forEach(config => {
          const canvas = canvasRefs.current[config.id];
          if (!canvas) return;
          chartInstancesRef.current[config.id] = new Chart(canvas, {
            type: config.type, data: config.data,
            options: {
              responsive: true, maintainAspectRatio: false,
              plugins: { legend: { position: 'top', labels: { color: '#475569', font: { size: 12 } } } },
              scales: config.type !== 'pie' && config.type !== 'doughnut' && config.type !== 'radar' ? {
                x: { ticks: { color: '#64748b', maxRotation: 45 }, grid: { color: '#e2e8f0' } },
                y: { ticks: { color: '#64748b' }, grid: { color: '#e2e8f0' } },
              } : undefined,
            },
          });
        });
      });
    }, 100);

    return () => clearTimeout(timer);
  }, [activeTab, chartConfigs]);

  // Cell reference string
  const cellRef = selectedCell ? `${colLetter(selectedCell.col)}${selectedCell.row + 1}` : '';
  const cellValue = selectedCell && sheetData ? (sheetData.rows[selectedCell.row]?.[selectedCell.col] ?? '') : '';

  // ─── Toolbar ───
  const Toolbar = () => (
    <div className="flex items-center gap-1 px-3 py-1.5 border-b border-gray-200 bg-gray-50 text-gray-600 text-xs flex-wrap">
      {/* Cell reference */}
      <div className="flex items-center border border-gray-300 rounded bg-white px-2 py-1 mr-2 min-w-[60px]">
        <span className="font-mono text-xs text-gray-700">{cellRef || 'A1'}</span>
      </div>

      <div className="w-px h-5 bg-gray-300 mx-1" />

      {/* Format buttons */}
      <button onClick={() => toggleFormat('bold')} title="Bold"
        className={`p-1.5 rounded hover:bg-gray-200 transition-colors ${getCellFormat(selectedCell?.row, selectedCell?.col).bold ? 'bg-gray-200 text-gray-900' : ''}`}>
        <Bold className="w-3.5 h-3.5" />
      </button>
      <button onClick={() => toggleFormat('italic')} title="Italic"
        className={`p-1.5 rounded hover:bg-gray-200 transition-colors ${getCellFormat(selectedCell?.row, selectedCell?.col).italic ? 'bg-gray-200 text-gray-900' : ''}`}>
        <Italic className="w-3.5 h-3.5" />
      </button>

      <div className="w-px h-5 bg-gray-300 mx-1" />

      {/* Alignment */}
      <button onClick={() => setAlignment('left')} title="Align Left"
        className={`p-1.5 rounded hover:bg-gray-200 transition-colors ${getCellFormat(selectedCell?.row, selectedCell?.col).align === 'left' ? 'bg-gray-200' : ''}`}>
        <AlignLeft className="w-3.5 h-3.5" />
      </button>
      <button onClick={() => setAlignment('center')} title="Align Center"
        className={`p-1.5 rounded hover:bg-gray-200 transition-colors ${getCellFormat(selectedCell?.row, selectedCell?.col).align === 'center' ? 'bg-gray-200' : ''}`}>
        <AlignCenter className="w-3.5 h-3.5" />
      </button>
      <button onClick={() => setAlignment('right')} title="Align Right"
        className={`p-1.5 rounded hover:bg-gray-200 transition-colors ${getCellFormat(selectedCell?.row, selectedCell?.col).align === 'right' ? 'bg-gray-200' : ''}`}>
        <AlignRight className="w-3.5 h-3.5" />
      </button>

      <div className="w-px h-5 bg-gray-300 mx-1" />

      {/* Text color */}
      <div className="relative group/color">
        <button title="Text Color"
          className="p-1.5 rounded hover:bg-gray-200 transition-colors flex items-center gap-0.5">
          <Type className="w-3.5 h-3.5" />
          <div className="w-3.5 h-1 rounded-sm" style={{ backgroundColor: getCellFormat(selectedCell?.row, selectedCell?.col).color || '#000000' }} />
        </button>
        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-2 hidden group-hover/color:grid grid-cols-6 gap-1 z-50 w-[156px]">
          {['#000000','#434343','#666666','#999999','#b7b7b7','#cccccc',
            '#e06666','#f6b26b','#ffd966','#93c47d','#76a5af','#6fa8dc',
            '#cc0000','#e69138','#f1c232','#6aa84f','#45818e','#3d85c6',
            '#990000','#b45f06','#bf9000','#38761d','#134f5c','#0b5394',
            '#660000','#783f04','#7f6000','#274e13','#0c343d','#073763'].map(c => (
            <button key={c} onClick={() => setTextColor(c)} title={c}
              className="w-5 h-5 rounded border border-gray-200 hover:scale-110 transition-transform"
              style={{ backgroundColor: c }} />
          ))}
        </div>
      </div>

      <div className="w-px h-5 bg-gray-300 mx-1" />

      {/* Insert */}
      <button onClick={addRow} title="Insert Row" className="p-1.5 rounded hover:bg-gray-200 transition-colors flex items-center gap-1">
        <Rows className="w-3.5 h-3.5" /> <span>Row</span>
      </button>
      <button onClick={addColumn} title="Insert Column" className="p-1.5 rounded hover:bg-gray-200 transition-colors flex items-center gap-1">
        <Columns className="w-3.5 h-3.5" /> <span>Column</span>
      </button>

      {selectedCell && sheetData && (
        <>
          <div className="w-px h-5 bg-gray-300 mx-1" />
          <button onClick={() => deleteRow(selectedCell.row)} title="Delete Row" className="p-1.5 rounded hover:bg-red-100 hover:text-red-600 transition-colors flex items-center gap-1">
            <Trash2 className="w-3.5 h-3.5" /> <span>Row</span>
          </button>
          <button onClick={() => deleteColumn(selectedCell.col)} title="Delete Column" className="p-1.5 rounded hover:bg-red-100 hover:text-red-600 transition-colors flex items-center gap-1">
            <Trash2 className="w-3.5 h-3.5" /> <span>Col</span>
          </button>
        </>
      )}

      <div className="w-px h-5 bg-gray-300 mx-1" />

      {/* Sort */}
      {selectedCell && (
        <>
          <button onClick={() => sortColumn(selectedCell.col, 'asc')} title="Sort Ascending" className="p-1.5 rounded hover:bg-gray-200 transition-colors">
            <ChevronUp className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => sortColumn(selectedCell.col, 'desc')} title="Sort Descending" className="p-1.5 rounded hover:bg-gray-200 transition-colors">
            <ChevronDown className="w-3.5 h-3.5" />
          </button>
        </>
      )}

      {/* Column type selector */}
      {selectedCell && sheetData && (
        <>
          <div className="w-px h-5 bg-gray-300 mx-1" />
          <select
            value={sheetData.column_types?.[selectedCell.col] || 'text'}
            onChange={(e) => changeColumnType(selectedCell.col, e.target.value)}
            className="text-xs border border-gray-300 rounded bg-white px-1.5 py-1 outline-none"
          >
            <option value="text">Text</option>
            <option value="number">Number</option>
            <option value="currency">Currency</option>
            <option value="percentage">Percent</option>
            <option value="date">Date</option>
          </select>
        </>
      )}

      {/* Formula bar */}
      <div className="flex-1 flex items-center ml-2 border border-gray-300 rounded bg-white px-2 py-1 min-w-[200px]">
        <span className="text-gray-400 mr-2 text-[11px] font-mono italic">fx</span>
        <input
          className="flex-1 text-xs text-gray-700 bg-transparent border-none outline-none"
          value={selectedCell && sheetData ? String(sheetData.rows[selectedCell.row]?.[selectedCell.col] ?? '') : ''}
          onChange={(e) => {
            if (!selectedCell || !sheetData) return;
            const { row, col } = selectedCell;
            const type = sheetData.column_types?.[col] || 'text';
            const isNumeric = type === 'number' || type === 'currency' || type === 'percentage';
            const val = isNumeric ? (e.target.value === '' ? '' : Number(e.target.value) || e.target.value) : e.target.value;
            handleCellChange(row, col, val);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); }
            else if (e.key === 'Escape') { e.target.blur(); }
          }}
          placeholder={selectedCell ? 'Enter value...' : ''}
          readOnly={!selectedCell}
        />
      </div>

      {/* File upload */}
      <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={handleFileUpload} />
      <button onClick={() => fileInputRef.current?.click()} title="Import CSV/Excel"
        className="p-1.5 rounded hover:bg-gray-200 transition-colors flex items-center gap-1 ml-1">
        <Upload className="w-3.5 h-3.5" /> <span>Import</span>
      </button>
    </div>
  );

  // ─── Spreadsheet Table (styled) ───
  // Arabic data renders right-to-left: detect Arabic in the title/headers and
  // flip the table direction (cells keep their own alignment).
  const sheetIsRtl = /[؀-ۿݐ-ݿࢠ-ࣿﭐ-﷿ﹰ-﻿]/.test(
    `${sheetData?.title || ''} ${(sheetData?.columns || []).join(' ')}`
  );
  const SpreadsheetTable = () => (
    <div className="overflow-auto flex-1 bg-white" dir={sheetIsRtl ? 'rtl' : undefined}>
      <table className="w-full border-collapse text-sm">
        <thead className="sticky top-0 z-10">
          {/* Column letters row */}
          <tr>
            <th className="w-12 px-2 py-1 text-center text-[11px] text-gray-400 border border-gray-200 bg-gray-100 font-normal" />
            {sheetData.columns.map((col, ci) => (
              <th key={ci} className={`px-2 py-1 text-center text-[11px] font-medium border border-gray-200 bg-gray-100 min-w-[120px] cursor-pointer hover:bg-gray-200 transition-colors ${
                selectedCell?.col === ci ? 'bg-blue-100 text-blue-700' : 'text-gray-400'
              }`}
                onClick={() => setSelectedCell({ row: 0, col: ci })}
              >
                {colLetter(ci)}
              </th>
            ))}
            <th className="w-8 border border-gray-200 bg-gray-100">
              <button onClick={addColumn} className="w-full flex items-center justify-center text-gray-400 hover:text-emerald-600" title="Add column">
                <Plus className="w-3 h-3" />
              </button>
            </th>
          </tr>
          {/* Styled header row with color */}
          <tr>
            <td className="px-2 py-2 text-center text-[11px] border border-gray-200 font-normal"
              style={{ backgroundColor: headerColor.bg + '15', color: headerColor.bg }}>
            </td>
            {sheetData.columns.map((col, ci) => {
              const TypeIcon = TYPE_ICONS[sheetData.column_types?.[ci]] || Type;
              return (
                <td key={ci} className="px-3 py-2 border border-gray-200 group"
                  style={{ backgroundColor: headerColor.bg, color: headerColor.text }}>
                  <div className="flex items-center gap-1.5">
                    <TypeIcon className="w-3 h-3 flex-shrink-0 opacity-70" />
                    <input
                      className="bg-transparent border-none outline-none font-semibold text-xs w-full placeholder-white/60"
                      style={{ color: headerColor.text }}
                      value={col}
                      onChange={(e) => handleHeaderChange(ci, e.target.value)}
                    />
                    <button onClick={() => sortColumn(ci, 'asc')}
                      className="opacity-0 group-hover:opacity-100 p-0.5 hover:bg-white/20 rounded transition-opacity" title="Sort">
                      <ArrowUpDown className="w-3 h-3" />
                    </button>
                  </div>
                </td>
              );
            })}
            <td className="border border-gray-200" style={{ backgroundColor: headerColor.bg + '30' }} />
          </tr>
        </thead>
        <tbody>
          {sheetData.rows.map((row, ri) => (
            <tr key={ri} className={`group/row transition-colors ${ri % 2 === 0 ? 'bg-white' : 'bg-gray-50/60'} hover:bg-blue-50/40`}>
              <td className={`px-2 py-1.5 text-center text-[11px] border border-gray-200 font-normal select-none ${
                selectedCell?.row === ri ? 'text-white font-medium' : 'text-gray-400 bg-gray-50'
              }`}
                style={selectedCell?.row === ri ? { backgroundColor: headerColor.bg, color: '#fff' } : {}}>
                {ri + 1}
              </td>
              {row.map((cell, ci) => {
                const isActive = editingCell?.row === ri && editingCell?.col === ci;
                const isSelected = selectedCell?.row === ri && selectedCell?.col === ci;
                const type = sheetData.column_types?.[ci] || 'text';
                const isNumeric = type === 'number' || type === 'currency' || type === 'percentage';
                const fmt = getCellFormat(ri, ci);

                return (
                  <td key={ci}
                    className={`px-3 py-1.5 border cursor-cell transition-colors ${
                      isActive ? 'border-2 bg-white ring-1 ring-blue-400' :
                      isSelected ? 'border-blue-400 bg-blue-50/50' :
                      'border-gray-200'
                    }`}
                    style={{
                      ...(isActive ? { borderColor: headerColor.bg } : isSelected ? { borderColor: headerColor.bg + '80' } : {}),
                      ...(fmt.align ? { textAlign: fmt.align } : {}),
                    }}
                    onClick={() => { setSelectedCell({ row: ri, col: ci }); setEditingCell({ row: ri, col: ci }); setContextMenu(null); }}
                    onContextMenu={(e) => { e.preventDefault(); setSelectedCell({ row: ri, col: ci }); setContextMenu({ x: e.clientX, y: e.clientY, row: ri, col: ci }); }}
                  >
                    {isActive ? (
                      <input autoFocus
                        className="w-full bg-transparent border-none outline-none text-gray-800 text-sm"
                        value={cell ?? ''}
                        onChange={(e) => {
                          const val = isNumeric ? (e.target.value === '' ? '' : Number(e.target.value) || e.target.value) : e.target.value;
                          handleCellChange(ri, ci, val);
                        }}
                        onBlur={() => setEditingCell(null)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.preventDefault(); setEditingCell(ri + 1 < sheetData.rows.length ? { row: ri + 1, col: ci } : null); setSelectedCell({ row: Math.min(ri + 1, sheetData.rows.length - 1), col: ci }); }
                          else if (e.key === 'Tab') { e.preventDefault(); const nc = ci + 1 < sheetData.columns.length ? ci + 1 : 0; setEditingCell({ row: ri, col: nc }); setSelectedCell({ row: ri, col: nc }); }
                          else if (e.key === 'Escape') { setEditingCell(null); }
                        }}
                        type={isNumeric ? 'number' : 'text'}
                        step="any"
                      />
                    ) : (
                      <span className={`text-sm ${fmt.bold ? 'font-bold' : ''} ${fmt.italic ? 'italic' : ''} ${
                        isNumeric ? 'font-mono tabular-nums' : ''
                      }`}
                        style={{ color: fmt.color || (isNumeric ? '#374151' : '#1f2937'), textAlign: fmt.align || undefined }}>
                        {formatCellValue(cell, ci)}
                      </span>
                    )}
                  </td>
                );
              })}
              <td className="border border-gray-200 w-8" />
            </tr>
          ))}
        </tbody>
      </table>
      <div className="p-1.5 border-t border-gray-200 bg-gray-50">
        <button onClick={addRow}
          className="flex items-center gap-1 text-[11px] text-gray-400 hover:text-emerald-600 px-2 py-1 rounded hover:bg-gray-100 transition-colors">
          <Plus className="w-3 h-3" /> Add row
        </button>
      </div>

      {/* Right-click context menu */}
      {contextMenu && (
        <div className="fixed inset-0 z-50" onClick={() => setContextMenu(null)} onContextMenu={(e) => { e.preventDefault(); setContextMenu(null); }}>
          <div className="absolute bg-white border border-gray-200 rounded-lg shadow-xl py-1 min-w-[180px] text-xs"
            style={{ left: contextMenu.x, top: contextMenu.y }}
            onClick={(e) => e.stopPropagation()}>
            <button className="w-full text-left px-3 py-1.5 hover:bg-gray-100 flex items-center gap-2"
              onClick={() => { insertRowAt(contextMenu.row); setContextMenu(null); }}>
              <Rows className="w-3.5 h-3.5 text-gray-500" /> Insert row above
            </button>
            <button className="w-full text-left px-3 py-1.5 hover:bg-gray-100 flex items-center gap-2"
              onClick={() => { insertRowAt(contextMenu.row + 1); setContextMenu(null); }}>
              <Rows className="w-3.5 h-3.5 text-gray-500" /> Insert row below
            </button>
            <div className="border-t border-gray-100 my-1" />
            <button className="w-full text-left px-3 py-1.5 hover:bg-gray-100 flex items-center gap-2"
              onClick={() => { insertColumnAt(contextMenu.col); setContextMenu(null); }}>
              <Columns className="w-3.5 h-3.5 text-gray-500" /> Insert column left
            </button>
            <button className="w-full text-left px-3 py-1.5 hover:bg-gray-100 flex items-center gap-2"
              onClick={() => { insertColumnAt(contextMenu.col + 1); setContextMenu(null); }}>
              <Columns className="w-3.5 h-3.5 text-gray-500" /> Insert column right
            </button>
            <div className="border-t border-gray-100 my-1" />
            <button className="w-full text-left px-3 py-1.5 hover:bg-red-50 hover:text-red-600 flex items-center gap-2"
              onClick={() => { deleteRow(contextMenu.row); setContextMenu(null); }}>
              <Trash2 className="w-3.5 h-3.5" /> Delete row
            </button>
            <button className="w-full text-left px-3 py-1.5 hover:bg-red-50 hover:text-red-600 flex items-center gap-2"
              onClick={() => { deleteColumn(contextMenu.col); setContextMenu(null); }}>
              <Trash2 className="w-3.5 h-3.5" /> Delete column
            </button>
          </div>
        </div>
      )}
    </div>
  );

  // ─── Visualize Tab ───
  const VisualizeTab = () => (
    <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
      {chartConfigs.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-full text-gray-400">
          <BarChart3 className="w-12 h-12 mb-3 opacity-40" />
          <p className="text-sm">No chart configurations found.</p>
          <p className="text-xs text-gray-500 mt-1">Use AI to add charts: "Add a bar chart for revenue by month"</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-6xl mx-auto">
          {chartConfigs.map(config => (
            <div key={config.id} className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
              <h3 className="text-sm font-semibold text-gray-700 mb-4">{config.title}</h3>
              <div style={{ height: 320 }}>
                <canvas ref={el => { canvasRefs.current[config.id] = el; }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // ─── AI Sidebar (left panel, Genspark-style) ───
  const AiSidebar = () => (
    <div className={`flex flex-col border-r border-gray-200 bg-white transition-all duration-200 ${aiPanelOpen ? 'w-[320px]' : 'w-[48px]'} flex-shrink-0`}>
      {/* Toggle header */}
      <button
        onClick={() => setAiPanelOpen(!aiPanelOpen)}
        className="flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 border-b border-gray-200 flex-shrink-0"
      >
        {aiPanelOpen ? (
          <>
            <Sparkles className="w-4 h-4 text-emerald-500" />
            <span className="flex-1 text-left">AI Sheets</span>
            <PanelLeftClose className="w-4 h-4 text-gray-400" />
          </>
        ) : (
          <PanelLeft className="w-4 h-4 text-gray-500 mx-auto" />
        )}
      </button>

      {aiPanelOpen && (
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Chat history */}
          <div className="flex-1 overflow-y-auto px-3 py-3">
            {chatHistory.length === 0 ? (
              <div className="flex flex-col gap-3 mt-2">
                <div className="text-center mb-2">
                  <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center mx-auto mb-2">
                    <Sparkles className="w-5 h-5 text-emerald-500" />
                  </div>
                  <p className="text-xs text-gray-500">Ask AI to modify your spreadsheet</p>
                </div>
                <div className="space-y-1.5">
                  {['Add a total row', 'Sort by highest value', 'Add a chart', 'Calculate averages', 'Add a % change column', 'Highlight top 3 values'].map((s, i) => (
                    <button key={i} onClick={() => setEditPrompt(s)}
                      className="w-full text-left text-xs px-3 py-2 rounded-lg border border-gray-100 hover:border-emerald-300 hover:bg-emerald-50/50 text-gray-600 transition-colors flex items-center gap-2">
                      <MessageSquare className="w-3 h-3 text-gray-400 flex-shrink-0" />
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {chatHistory.map((msg, i) => (
                  <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[90%] text-xs px-3 py-2 rounded-xl ${
                      msg.role === 'user'
                        ? 'bg-emerald-500 text-white rounded-br-sm'
                        : msg.error
                          ? 'bg-red-50 text-red-600 border border-red-100 rounded-bl-sm'
                          : 'bg-gray-100 text-gray-700 rounded-bl-sm'
                    }`}>
                      {msg.role === 'user' ? msg.content : (
                        <div className="[&_p]:mb-1 [&_p:last-child]:mb-0 [&_ul]:ml-3 [&_ul]:list-disc [&_ol]:ml-3 [&_ol]:list-decimal [&_li]:mb-0.5 [&_strong]:font-semibold">
                          <ReactMarkdown>{msg.content}</ReactMarkdown>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {isEditing && (
                  <div className="flex justify-start">
                    <div className="bg-gray-100 text-gray-600 text-xs px-3 py-2 rounded-xl rounded-bl-sm flex items-center gap-2">
                      <Loader2 className="w-3 h-3 animate-spin" /> Working...
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>
            )}
          </div>

          {/* Input area at bottom of sidebar */}
          <div className="p-3 border-t border-gray-100 flex-shrink-0">
            <div className="relative">
              <textarea
                ref={editTextareaRef}
                value={editPrompt}
                onChange={(e) => setEditPrompt(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleEditRequest(); } }}
                placeholder="Ask anything, create anything..."
                rows={2}
                className="w-full text-sm bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 pr-10 resize-none focus:outline-none focus:ring-2 focus:ring-emerald-400/50 focus:border-emerald-400 text-gray-700 placeholder-gray-400"
              />
              <button onClick={handleEditRequest} disabled={!editPrompt.trim() || isEditing}
                className="absolute right-2 bottom-2 w-7 h-7 rounded-lg bg-emerald-500 text-white flex items-center justify-center hover:bg-emerald-600 transition-colors disabled:opacity-30">
                <Send className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className={`${externalChatMode ? 'h-full' : 'h-screen'} flex flex-col bg-white text-gray-800`}>
      {/* Header */}
      {/* Header — hidden in externalChatMode (UnifiedWorkspace toolbar handles it) */}
      {!externalChatMode && (
      <div className="flex items-center gap-3 px-4 py-2.5 border-b border-gray-200 bg-white">
        <Button variant="ghost" size="sm" onClick={onBack} className="gap-1.5 text-gray-600 hover:text-gray-900">
          <ArrowLeft className="w-4 h-4" /> Back
        </Button>
        <div className="flex-1 min-w-0">
          <h2 className="text-sm font-semibold text-gray-900 truncate">{title}</h2>
          <p className="text-[11px] text-gray-500">
            {phase === 'generating' ? 'Generating...' :
              sheetData ? `${sheetData.rows?.length || 0} rows × ${sheetData.columns?.length || 0} columns` : ''}
          </p>
        </div>

        {phase === 'ready' && sheetData && (
          <div className="flex items-center gap-1.5">
            <Button variant="outline" size="sm" onClick={() => { setIsSaving(true); handleSaveProject().finally(() => setIsSaving(false)); }} disabled={isSaving} className="text-xs h-8">
              <Save className={`w-3.5 h-3.5 mr-1 ${isSaving ? 'animate-spin' : ''}`} /> Save
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowShareModal(true)} className="text-xs h-8">
              <Share2 className="w-3.5 h-3.5 mr-1" /> Share
            </Button>
            <Button size="sm" onClick={handleExportXLSX} className="text-xs h-8 bg-gradient-to-r from-emerald-500 to-green-500 text-white hover:from-emerald-600 hover:to-green-600">
              <FileSpreadsheet className="w-3.5 h-3.5 mr-1" /> Excel
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportPDF} className="text-xs h-8">
              <FileDown className="w-3.5 h-3.5 mr-1" /> PDF
            </Button>
          </div>
        )}
      </div>
      )}

      {/* Main layout: AI sidebar left + spreadsheet right */}
      <div className="flex-1 flex overflow-hidden">
        {/* AI Sidebar — always present when ready */}
        {phase === 'ready' && sheetData && !externalChatMode && <AiSidebar />}

        {/* Right side: tabs + content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Tabs */}
          {phase === 'ready' && sheetData && (
            <>
              <div className="flex items-center border-b border-gray-200 px-4 bg-white">
                <button onClick={() => setActiveTab('spreadsheet')}
                  className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'spreadsheet' ? 'border-emerald-500 text-emerald-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                  <Table2 className="w-4 h-4" /> Spreadsheet
                </button>
                <button onClick={() => setActiveTab('visualize')}
                  className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'visualize' ? 'border-emerald-500 text-emerald-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                  <BarChart3 className="w-4 h-4" /> Visualize
                  {chartConfigs.length > 0 && (
                    <span className="text-[10px] bg-emerald-100 text-emerald-600 px-1.5 rounded-full">{chartConfigs.length}</span>
                  )}
                </button>
              </div>
              {activeTab === 'spreadsheet' && <Toolbar />}
            </>
          )}

          {/* Content area */}
          {phase === 'generating' && (
            <div className="flex flex-col items-center justify-center h-full bg-gray-50">
              <div className="relative">
                <Loader2 className="w-12 h-12 text-emerald-500 animate-spin" />
                <Sparkles className="w-5 h-5 text-emerald-400 absolute -top-1 -right-1 animate-pulse" />
              </div>
              <p className="text-sm text-gray-500 mt-4">{generationStatus}</p>
            </div>
          )}

          {phase === 'error' && (
            <div className="flex flex-col items-center justify-center h-full bg-gray-50">
              <p className="text-sm text-red-500 mb-3">Failed to generate spreadsheet</p>
              <Button variant="outline" onClick={generateSheetData}>
                <RefreshCw className="w-4 h-4 mr-2" /> Try Again
              </Button>
            </div>
          )}

          {phase === 'ready' && sheetData && (
            <>
              <div className={`flex-1 overflow-auto ${activeTab !== 'spreadsheet' ? 'hidden' : ''}`}>
                <SpreadsheetTable />
              </div>
              <div className={`flex-1 overflow-auto ${activeTab !== 'visualize' ? 'hidden' : ''}`}>
                <VisualizeTab />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Sheet tabs at bottom */}
      {phase === 'ready' && sheetData && (
        <div className="flex items-center gap-1 px-4 py-1 border-t border-gray-200 bg-gray-50 text-[11px] text-gray-500">
          <div className="px-3 py-0.5 bg-white border border-gray-200 rounded text-gray-700 font-medium">Sheet1</div>
          <button className="px-1.5 py-0.5 hover:bg-gray-200 rounded transition-colors" title="Add sheet">
            <Plus className="w-3 h-3" />
          </button>
          <div className="flex-1" />
          <span>{sheetData.rows?.length || 0} rows × {sheetData.columns?.length || 0} cols</span>
        </div>
      )}
      <ShareContentModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        contentType="sheets"
        title={title || 'Spreadsheet'}
        userId={userId}
        htmlContent=""
      />
    </div>
  );
});

export default SheetsWorkspace;
